import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  Flex,
  IconButton,
  FormErrorMessage,
  useToast,
  InputGroup,
  InputRightElement,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "../../Login.css";
import { performLogin } from "../../Services/auth";
import { useLang } from "../../Hooks";
import { useAuth } from "../../Contexts/AuthContext";
import { Styles } from "../../Config/branding";
import LangSwitch from "../../Components/LangSwitch";
import useBranding from "../../Hooks/useBranding";

const LoginPage = () => {
  const toast = useToast();
  const { texts } = useLang();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { Name } = useBranding();
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    const result = await performLogin(email, password);
    if (result.success) {
      toast({
        title: texts.login_success,
        description: texts.welcome_back,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      login(result.user, result.token);
    } else {
      setError(result.error);
      toast({
        title: texts.login_failed,
        description: result.error,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
    setIsLoading(false);
  };

   return (
    <Box 
      className="login-bg"
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      w="100vw"
      h="100vh"
      minH="100vh"
      m={0}
      p={0}
    >
      <Box
        className="login-glass"
        as="form"
        onSubmit={handleSubmit}
        w={{ base: "92%", sm: "400px", md: "420px" }}
        maxW="420px"
        m="0 !important"
        p={{ base: "25px 20px", md: "40px" }}
      >
        <Flex justify="flex-end" mb={{ base: 1, md: 2 }}>
          <LangSwitch size={{ base: "sm", md: "md" }} />
        </Flex>

        <Stack spacing={{ base: 4, md: 5 }} color="white">
          <VStack spacing={{ base: 1, md: 2 }} mb={{ base: 3, md: 4 }}>
            <Heading
              textAlign="center"
              fontSize={{ base: "lg", sm: "xl", md: "2xl" }}
              fontWeight="bold"
              lineHeight="shorter"
            >
              {texts.welcome?.replace("{{Name}}", Name) ??
                `Welcome to ${Name} CRM`}
            </Heading>
            <Text 
              color="whiteAlpha.800" 
              textAlign="center" 
              fontSize={{ base: "sm", md: "md" }}
              lineHeight="shorter"
            >
              {texts.login_to_manage ?? "Login to manage your clients"}
            </Text>
          </VStack>
          <FormControl isInvalid={!!error}>
            <FormLabel 
              fontSize={{ base: "sm", md: "sm" }} 
              color="whiteAlpha.900"
              mb={{ base: 1, md: 2 }}
            >
              {texts.email}
            </FormLabel>
            <Input
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              bg="var(--login-input-bg)"
              border="none"
              _placeholder={{ color: "var(--login-input-placeholder-color)" }}
              _focus={{ bg: "var(--login-input-focus-bg)", border: "none" }}
              isRequired
              borderRadius="md"
              height={{ base: "50px", md: "42px" }} 
              fontSize={{ base: "16px", md: "sm" }}
            />
          </FormControl>

          <FormControl isInvalid={!!error}>
            <FormLabel 
              fontSize={{ base: "sm", md: "sm" }} 
              color="whiteAlpha.900"
              mb={{ base: 1, md: 2 }}
            >
              {texts.password}
            </FormLabel>
            
            <Box 
              position="relative"
              width="100%"
              height={{ base: "50px", md: "42px" }}
              border="1px solid transparent"
            >
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                bg="var(--login-input-bg)"
                border="none"
                _placeholder={{ color: "var(--login-input-placeholder-color)" }}
                _focus={{ 
                  bg: "var(--login-input-focus-bg)", 
                  border: "none",
                  boxShadow: "none",
                  outline: "none"
                }}
                isRequired
                borderRadius="md"
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                width="100%"
                height="100%"
                fontSize="16px"
                pr="50px" 
                pl="16px"
              />
              
              <Box
                position="absolute"
                right="8px"
                top="50%"
                transform="translateY(-50%)"
                zIndex={10}
                width="44px"
                height="44px"
                display="flex"
                alignItems="center"
                justifyContent="center"

              >
                <Box
                  as="button"
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  width="100%"
                  height="100%"
                  minWidth="44px"
                  minHeight="44px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="rgba(255, 255, 255, 0.7)"
                  _hover={{ 
                    color: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.1)"
                  }}
                  _active={{ 
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    transform: "scale(0.95)"
                  }}
                  borderRadius="md"
                  transition="all 0.2s"
                  bg="transparent"
                  border="none"
                  cursor="pointer"
                  padding="0"
                  sx={{
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                    outline: 'none'
                  }}
                >
                  {showPassword ? 
                    <FaEyeSlash size={20} /> : 
                    <FaEye size={20} />
                  }
                </Box>
              </Box>
            </Box>
            
            {error && (
              <FormErrorMessage mt={2} fontSize={{ base: "xs", md: "sm" }} color="red.300">
                {error}
              </FormErrorMessage>
            )}
          </FormControl>

          <Button
            type="submit"
            bg={Styles.button?.bg || "teal.400"}
            color="white"
            _hover={{ bg: Styles.button?.hover || "teal.500" }}
            _active={{ bg: Styles.button?.active || "teal.600" }}
            isLoading={isLoading}
            size={{ base: "md", md: "lg" }}
            w="full"
            mt={{ base: 3, md: 4 }}
            boxShadow="md"
            height={{ base: "50px", md: "48px" }} 
            fontSize={{ base: "16px", md: "16px" }}
          >
            {texts.login}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default LoginPage;
